import React, { useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Stack,
  InputAdornment,
  IconButton,
  Box
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { VisibilityIcon as Visibility, VisibilityOffIcon as VisibilityOff } from "../../icons";
import { useRootStore } from "../../store/contexts";
import { usePasswordValidation, PasswordRequirementsList } from "../../auth";
import { TrustedDevicesSection } from "./TrustedDevicesSection";

export const SecuritySettingsPage = observer(() => {
  const rootStore = useRootStore();
  const { authStore, langStore } = rootStore;

  const [showPassword, setShowPassword] = useState(false);

  const newPassword = authStore.newPassword || '';
  const validation = usePasswordValidation(newPassword, authStore.secondPassword);

  const handleChangePasswordClick = async () => {
    if (!validation.isValid) return;
    await authStore.changePassword();
    if (!authStore.errorMessage) {
      await authStore.logout();
      rootStore.router.goTo("/");
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        {langStore.changePasswordLabel}
      </Typography>

      <Stack spacing={2}>
        <TextField
          autoComplete="current-password"
          label={langStore.currentPasswordLabel}
          onChange={(e) => authStore.setPassword(e.target.value)}
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          fullWidth
          required
        />

        <TextField
          autoComplete="new-password"
          label={langStore.newPasswordLabel}
          onChange={(e) => authStore.setNewPassword(e.target.value)}
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          fullWidth
          required
        />

        <TextField
          autoComplete="new-password"
          label={langStore.confirmPasswordLabel}
          onChange={(e) => authStore.setSecondPassword(e.target.value)}
          type={showPassword ? "text" : "password"}
          error={authStore.secondPassword.length > 0 && !validation.passwordsMatch}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          fullWidth
          required
        />

        <PasswordRequirementsList
          validation={validation}
          labels={{
            minLength: langStore.passwordMinLength,
            uppercase: langStore.passwordRequiresUppercase,
            lowercase: langStore.passwordRequiresLowercase,
            number: langStore.passwordRequiresNumber,
            special: langStore.passwordRequiresSpecial,
            notCommon: langStore.passwordNotCommon,
            match: langStore.passwordsMatchLabel
          }}
        />

        {authStore.errorMessage && (
          <Typography color="error" sx={{ textAlign: 'center' }}>
            {authStore.errorMessage}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleChangePasswordClick}
            disabled={!validation.isValid || !authStore.password}
          >
            {langStore.changePasswordLabel}
          </Button>
        </Box>
      </Stack>

      <TrustedDevicesSection />
    </Box>
  );
});
